import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import MetaWhatsappEmbeddedSignup from '@/components/integrations/MetaWhatsappEmbeddedSignup';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SectionHelp from '@/components/help/SectionHelp';
import HelpTooltip from '@/components/help/HelpTooltip';
import {
  Handshake, AlertTriangle, Globe, Palette, Building2, Copy, CheckCircle2, CreditCard,
} from 'lucide-react';

// S134 (4/N) — El portal del socio. Lo único que él ve de nuestro lado.
//
// Todo lo que se pide aquí sale de `/api/partner/*`, que resuelve la cartera
// por la credencial. Esta pantalla NUNCA manda un id de socio: si pudiera,
// mandaría el de otro.

const TARJETA = 'border rounded-lg p-4 bg-white';

function Copiable({ valor }) {
  const [copiado, setCopiado] = useState(false);
  return (
    <button type="button"
      onClick={() => {
        navigator.clipboard?.writeText(valor);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 1500);
      }}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded bg-slate-50 hover:bg-slate-100 font-mono break-all text-left">
      {copiado ? <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
               : <Copy className="w-3 h-3 shrink-0" />}
      <span>{valor}</span>
    </button>
  );
}

export default function PortalSocio() {
  const [pago, setPago] = useState(null);
  const [poniendoTarjeta, setPoniendoTarjeta] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [cargando, setCargando] = useState(true);
  const [datos, setDatos] = useState(null);
  const [dominio, setDominio] = useState('');
  const [catalogo, setCatalogo] = useState({ verticales: [], planes: [] });
  const [paquetes, setPaquetes] = useState([]);
  const [infra, setInfra] = useState([]);
  const [editPaq, setEditPaq] = useState(null);
  const [nuevoPaq, setNuevoPaq] = useState({ plan_id: '', name: '', price: '' });
  const [nuevoNeg, setNuevoNeg] = useState({ name: '', vertical: '', owner_name: '', owner_email: '', plan_id: '' });
  const [mostrandoAlta, setMostrandoAlta] = useState(false);
  const [marca, setMarca] = useState({});
  const [ocupado, setOcupado] = useState(false);
  const [remitente, setRemitente] = useState(null);
  const [mensajes, setMensajes] = useState(null);
  const [comprandoBloque, setComprandoBloque] = useState(null);
  const [telNeg, setTelNeg] = useState({});
  const [conversionLogo, setConversionLogo] = useState(null);
  const [remProveedor, setRemProveedor] = useState('');
  const [remClave, setRemClave] = useState('');
  const [remIdentidad, setRemIdentidad] = useState('');
  const [remAppName, setRemAppName] = useState('');
  const [remModo, setRemModo] = useState('plataforma');

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/partner/mi-cuenta');
      setDatos(data);
      setDominio(data?.dominio?.valor || '');
      api.get('/partner/catalogo').then((r) => setCatalogo(r.data || { verticales: [], planes: [] })).catch(() => {});
      api.get('/partner/paquetes').then((r) => setPaquetes(r.data?.paquetes || [])).catch(() => {});
      api.get('/partner/infraestructura').then((r) => setInfra(r.data?.proveedores || [])).catch(() => {});
      setMarca(data?.marca || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCargando(false); }

    // Aparte de la cuenta: consulta a Stripe y puede tardar. Si fuera parte de
    // la misma llamada, un Stripe lento dejaría el portal entero sin abrir.
    try {
      const { data } = await api.get('/partner/remitente-avisos');
      setRemitente(data);
      setRemProveedor(data?.remitente?.proveedor || 'gupshup');
      setRemIdentidad(data?.remitente?.identidad || '');
      setRemModo(data?.remitente && data.remitente.status === 'active' ? 'propio' : 'plataforma');
    } catch (e) { setRemitente({ aplica: false, motivo: formatApiError(e) }); }

    try {
      const { data } = await api.get('/partner/forma-de-pago');
      setPago(data);
    } catch (e) { setPago({ error: formatApiError(e) }); }

    try {
      const { data } = await api.get('/partner/mis-mensajes');
      setMensajes(data);
    } catch (e) { setMensajes({ error: formatApiError(e) }); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const r = searchParams.get('tarjeta');
    if (!r) return;
    if (r === 'ok') {
      toast.success('Tarjeta guardada. El cargo mensual sale el día de corte, no ahora.');
    } else if (r === 'cancelada') {
      toast.message('No se guardó la tarjeta. Puedes intentarlo cuando quieras.');
    }
    searchParams.delete('tarjeta');
    setSearchParams(searchParams, { replace: true });
    cargar();
  }, [searchParams, setSearchParams, cargar]);


  async function ponerMiTarjeta() {
    setPoniendoTarjeta(true);
    try {
      const { data } = await api.post('/partner/forma-de-pago/enlace');
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error('Stripe no devolvió el enlace. Intenta de nuevo.');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo abrir Stripe');
    } finally {
      setPoniendoTarjeta(false);
    }
  }

  async function quitarMiTarjeta() {
    if (!window.confirm('¿Quitar esta tarjeta? El cargo mensual no podrá salir hasta que pongas otra.')) return;
    setPoniendoTarjeta(true);
    try {
      await api.post('/partner/forma-de-pago/olvidar');
      toast.success('Tarjeta quitada.');
      const { data } = await api.get('/partner/forma-de-pago');
      setPago(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo quitar');
    } finally {
      setPoniendoTarjeta(false);
    }
  }

  async function guardarDominio() {
    setOcupado(true);
    try {
      const { data } = await api.put('/partner/dominio', { dominio });
      toast.success(data.aviso);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setOcupado(false); }
  }

  async function comprobarDominio() {
    setOcupado(true);
    try {
      const { data } = await api.post('/partner/dominio/comprobar');
      data.verificado ? toast.success(data.aviso) : toast.error(data.aviso);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setOcupado(false); }
  }

  async function usarNumeroPlataforma() {
    setOcupado(true);
    try {
      const { data } = await api.post('/partner/remitente-avisos/usar-plataforma');
      toast.success(data.aviso);
      setRemModo('plataforma');
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setOcupado(false); }
  }

  async function guardarRemitente() {
    if (remModo !== 'propio') {
      await usarNumeroPlataforma();
      return;
    }
    if (remProveedor === 'gupshup' && (!remAppName.trim() || !remClave.trim() || !remIdentidad.trim())) {
      toast.error('Gupshup pide 3 datos de TU cuenta: nombre de la app, API Key y el número.');
      return;
    }
    setOcupado(true);
    try {
      const { data } = await api.put('/partner/remitente-avisos', {
        modo: 'propio',
        proveedor: remProveedor,
        app_name: remAppName,
        api_key: remClave,
        source_phone: remIdentidad,
        identidad: remIdentidad || null,
        credentials: remProveedor === 'gupshup'
          ? { api_key: remClave, app_name: remAppName, source_phone: remIdentidad }
          : { clave: remClave },
      });
      toast.success(data.aviso);
      setRemClave('');
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setOcupado(false); }
  }

  async function subirMiLogo(archivo) {
    if (!archivo) return;
    setOcupado(true);
    setConversionLogo(null);
    try {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo);
      // El Content-Type va explícito: el cliente `api` pone json en todas las
      // peticiones y con json el backend no ve el archivo.
      const { data } = await api.post('/partner/remitente-avisos/logo', cuerpo,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setConversionLogo(data.conversion || null);
      toast.success('Logo guardado.');
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setOcupado(false); }
  }

  async function quitarMiLogo() {
    setOcupado(true);
    try {
      await api.delete('/partner/remitente-avisos/logo');
      setConversionLogo(null);
      toast.success('Logo quitado. El aviso saldrá solo con texto.');
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setOcupado(false); }
  }

  async function subirLogo(e, variante) {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('archivo', f);
    fd.append('variante', variante);
    setOcupado(true);
    try {
      const { data } = await api.post('/partner/marca/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(data.aviso);
      const r = await api.get('/partner/me');
      if (r.data?.marca) setMarca({ ...marca, ...r.data.marca });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setOcupado(false); e.target.value = ''; }
  }

  async function guardarMarca() {
    setOcupado(true);
    try {
      const { data } = await api.put('/partner/marca', marca);
      toast.success(data.aviso);
      await cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setOcupado(false); }
  }

  if (cargando) return <div className="p-6 text-slate-500">Cargando tu cuenta…</div>;

  const s = datos?.socio || {};
  const esPartner = s.tipo === 'partner';
  const aviso = datos?.aviso;
  const dom = datos?.dominio || {};
  const registros = dom.instrucciones?.registros || [];

  return (
    <div className="p-6 space-y-5 max-w-4xl" data-testid="portal-socio">
      <div className="flex items-center gap-2">
        <Handshake className="w-6 h-6 text-indigo-600" />
        <h1 className="text-2xl font-semibold">{s.nombre}</h1>
        <span className="text-xs px-2 py-1 rounded-full border bg-slate-50 text-slate-600">
          {esPartner ? 'Marca blanca' : 'Reseller'}
        </span>
      </div>

      {datos && datos.soy_titular === false && (
        <div className="border-l-4 border-amber-500 bg-amber-50 p-3 rounded" data-testid="socio-aviso-empleado">
          <div className="flex items-center gap-2 font-semibold text-amber-800">
            <AlertTriangle className="w-4 h-4" /> Estás en la cuenta como empleado
          </div>
          <p className="text-sm text-amber-800 mt-1">
            Puedes ver todo, dar de alta clientes y entrar a sus negocios. Lo que
            toca el dinero (la tarjeta y comprar mensajes) y la identidad de tu
            marca (nombre, logo, dominio, remitente e infraestructura) solo lo
            cambia el <strong>titular</strong> de la cuenta.
          </p>
        </div>
      )}

      {aviso && (
        <div className="border-l-4 border-rose-500 bg-rose-50 p-3 rounded">
          <div className="flex items-center gap-2 font-semibold text-rose-800">
            <AlertTriangle className="w-4 h-4" /> {aviso.titulo}
          </div>
          <p className="text-sm text-rose-800 mt-1">{aviso.texto}</p>
          {aviso.detalle && <p className="text-xs text-rose-700 mt-1">{aviso.detalle}</p>}
        </div>
      )}

      <SectionHelp
        module="portal-socio"
        title="tu cuenta de socio"
        description={datos?.como_funciona}
        whatItDoesNot={'no te deja entrar a la operación de tus clientes. Sus prospectos, '
                       + 'sus conversaciones y su caja son suyos, no se comparten contigo.'} />

      {/* ── Su bolsa de mensajes de WhatsApp ──
          Siempre visible: si no ha comprado, aquí se compra. */}
      <section id="mensajes" className={TARJETA} data-testid="socio-bolsa-mensajes">
        <h2 className="font-semibold mb-1 flex items-center gap-1">
          Tus mensajes de WhatsApp
          <HelpTooltip text="Tus negocios gastan de esta bolsa. Compras el bloque aquí, se carga a tu tarjeta y se abona al momento. Entrada y salida se compran aparte." />
        </h2>
        {mensajes?.error && (
          <p className="text-sm text-rose-700 mb-2">No se pudo cargar la bolsa: {mensajes.error}</p>
        )}
        <p className="text-xs text-slate-500 mb-3">
          {mensajes?.aviso || 'Compra un bloque para que tus negocios puedan escribir por WhatsApp.'}
        </p>
        {mensajes?.freno_saliente?.zona === 'sin_compra' && (
          <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
            <b>Aún no tienes mensajes.</b> Mientras no compres un bloque, el WhatsApp
            de tus negocios no sale: no gastamos nosotros por ti.
          </div>
        )}
        {mensajes?.freno_saliente?.zona === 'excedente' && (
          <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
            <b>Vas en excedente (deuda).</b>{' '}
            {(mensajes.freno_saliente.deuda_msgs || 0).toLocaleString('es-MX')} msgs
            sobre tu paquete. Recarga para no frenar envíos.
          </div>
        )}
        {mensajes?.freno_saliente?.zona === 'bloqueado' && (
          <div className="text-sm text-rose-900 bg-rose-50 border border-rose-200 rounded p-2 mb-3">
            <b>Envíos detenidos.</b> Recarga un bloque para que tus negocios vuelvan a escribir.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {['entrante', 'saliente'].map((d) => {
            const b = (mensajes?.saldo && mensajes.saldo[d]) || {};
            const pr = (mensajes?.proyeccion && mensajes.proyeccion[d]) || {};
            const comprado = b.comprado ?? 0;
            const gastado = b.gastado ?? 0;
            const restante = b.restante ?? 0;
            const pct = comprado > 0 ? Math.min(100, Math.round((gastado / comprado) * 100)) : 0;
            return (
              <div key={d} className="border border-slate-200 rounded-lg p-3">
                <div className="text-sm text-slate-500">
                  {d === 'entrante' ? 'Te escriben (entrada)' : 'Tú envías (salida)'}
                </div>
                <div className={`text-3xl font-semibold tabular-nums ${b.excedido ? 'text-amber-600' : 'text-[#0F172A]'}`}>
                  {restante.toLocaleString('es-MX')}
                  <span className="text-sm font-normal text-slate-500 ml-1">disponibles</span>
                </div>
                <div className="h-2 bg-slate-100 rounded mt-2 overflow-hidden">
                  <div className={`h-full ${pct > 90 ? 'bg-rose-500' : pct > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                       style={{ width: pct + '%' }} />
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {gastado.toLocaleString('es-MX')} usados de {comprado.toLocaleString('es-MX')} comprados
                  {pr.dias_restantes != null ? ` · a este ritmo, ${pr.dias_restantes} día(s)` : ''}
                </div>
              </div>
            );
          })}
        </div>

        <h3 className="text-sm font-medium mb-2">Comprar un bloque</h3>
        {!pago?.tarjeta && (
          <div className="text-sm bg-slate-50 border rounded p-2 mb-2 flex flex-wrap items-center justify-between gap-2">
            <span>Primero guarda tu tarjeta. El cargo es ahora y el bloque se abona al momento.</span>
            <Button size="sm" onClick={ponerMiTarjeta} disabled={poniendoTarjeta}>
              {poniendoTarjeta ? 'Abriendo…' : 'Agregar mi tarjeta'}
            </Button>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {(mensajes?.bloques || []).map((b) => (
            <div key={b.clave} className="border rounded-lg p-3 flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-medium">{b.mensajes.toLocaleString('es-MX')} {b.direccion === 'saliente' ? 'de salida' : 'de entrada'}</div>
                <div className="text-xs text-slate-500">${b.precio} {b.moneda}</div>
              </div>
              <Button size="sm" disabled={ocupado || comprandoBloque || !pago?.tarjeta}
                onClick={async () => {
                  const ok = window.confirm(
                    'Se cargarán $' + b.precio + ' ' + b.moneda
                    + ' a tu tarjeta' + (pago?.tarjeta?.ultimos4 ? ' ••••' + pago.tarjeta.ultimos4 : '')
                    + '. Se abonan ' + b.mensajes.toLocaleString('es-MX')
                    + ' mensajes de ' + (b.direccion === 'saliente' ? 'salida' : 'entrada') + '.'
                  );
                  if (!ok) return;
                  setComprandoBloque(b.clave);
                  setOcupado(true);
                  try {
                    const { data } = await api.post('/partner/mis-mensajes/comprar', {
                      bloque: b.clave,
                      referencia: 'compra-portal-' + Date.now() + '-' + b.clave,
                    });
                    toast.success(data.aviso || 'Bloque abonado');
                    const r = await api.get('/partner/mis-mensajes');
                    setMensajes(r.data);
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setComprandoBloque(null); setOcupado(false); }
                }}>
                {comprandoBloque === b.clave ? 'Cobrando…' : 'Comprar'}
              </Button>
            </div>
          ))}
          {!(mensajes?.bloques || []).length && !mensajes?.error && (
            <p className="text-xs text-slate-500">Cargando paquetes…</p>
          )}
        </div>

        {(mensajes?.por_negocio || []).length > 0 && (
          <div className="mb-1">
            <h3 className="text-sm font-medium mb-2">Lo que han usado tus negocios</h3>
            <div className="space-y-1">
              {mensajes.por_negocio.map((n) => (
                <div key={n.tenant_id || n.nombre} className="flex justify-between text-sm border-b border-slate-100 py-1">
                  <span className="truncate pr-2">{n.nombre}</span>
                  <span className="tabular-nums text-slate-600 shrink-0">
                    {(n.saliente || 0).toLocaleString('es-MX')} salida · {(n.entrante || 0).toLocaleString('es-MX')} entrada
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ── Su cuenta ── */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={TARJETA}>
          <div className="text-sm text-slate-500 flex items-center gap-1">
            Negocios activos
            <HelpTooltip text="Los que están vivos y cuentan para tu cuenta. Un negocio archivado o cancelado deja de contar." />
          </div>
          <div className="text-2xl font-semibold tabular-nums">{s.licencias_activas ?? 0}</div>
        </div>
        {esPartner ? (
          <>
            <div className={TARJETA}>
              <div className="text-sm text-slate-500 flex items-center gap-1">
                Tu mínimo
                <HelpTooltip text="Las licencias que pactaste pagar aunque tengas menos clientes. Es el piso del trato." />
              </div>
              <div className="text-2xl font-semibold tabular-nums">{s.minimo}</div>
            </div>
            <div className={TARJETA}>
              <div className="text-sm text-slate-500 flex items-center gap-1">
                Se te cobran
                <HelpTooltip text="Tus licencias activas o tu mínimo, lo que sea mayor." />
              </div>
              <div className="text-2xl font-semibold tabular-nums">{s.se_le_cobrarian}</div>
            </div>
            <div className={`${TARJETA} ${s.paga_el_minimo ? 'border-amber-300 bg-amber-50/50' : ''}`}>
              <div className="text-sm text-slate-500">Tu mes</div>
              <div className="text-2xl font-semibold tabular-nums">
                {s.total_del_mes} <span className="text-sm">{s.moneda}</span>
              </div>
              {s.paga_el_minimo && (
                <div className="text-xs text-amber-800 mt-1">
                  Estás pagando el mínimo: te sobran licencias por vender.
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className={TARJETA}>
              <div className="text-sm text-slate-500 flex items-center gap-1">
                Primera venta
                <HelpTooltip text="El porcentaje que ganas del primer pago de cada cliente que traes." />
              </div>
              <div className="text-2xl font-semibold tabular-nums">
                {s.comision_primera_venta}%
              </div>
            </div>
            <div className={TARJETA}>
              <div className="text-sm text-slate-500 flex items-center gap-1">
                Cada mes siguiente
                <HelpTooltip text="Lo que ganas de cada mensualidad posterior, mientras el cliente siga pagando. Se aplica sobre el pago completo, así que si sube de paquete tu comisión sube sola." />
              </div>
              <div className="text-2xl font-semibold tabular-nums">
                {s.comision_meses_siguientes}%
              </div>
            </div>
          </>
        )}
      </section>

      {/* ── Su estado de cuenta ── */}
      {esPartner ? (
        <section className="space-y-2">
          <h2 className="font-medium flex items-center gap-1">
            Tus cargos de licencias
            <HelpTooltip text="Cada mes se te genera un cargo. Las tres cifras van por separado para que puedas comprobar de dónde sale el total." />
          </h2>
          {(datos.cargos || []).length === 0 ? (
            <div className={`${TARJETA} text-sm text-slate-600`}>
              Todavía no tienes cargos. El primero se genera al cerrar tu primer mes completo.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Mes</th>
                    <th className="text-right px-3 py-2">Activas</th>
                    <th className="text-right px-3 py-2">Mínimo</th>
                    <th className="text-right px-3 py-2">Cobradas</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-right px-3 py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.cargos.map((c) => (
                    <tr key={c.id} className="border-t">
                      <td className="px-3 py-2">{c.periodo}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.activas}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.minimo}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">{c.cobradas}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.total} {c.moneda}</td>
                      <td className="px-3 py-2 text-right">
                        {c.estado === 'pending' ? 'por pagar'
                          : c.estado === 'paid' ? 'pagado' : c.estado}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : (
        <section className="space-y-2">
          <h2 className="font-medium flex items-center gap-1">
            Tus liquidaciones
            <HelpTooltip text="Se te paga a mes vencido: lo que ganas en agosto se liquida a partir del 1 de septiembre." />
          </h2>
          <p className="text-xs text-slate-600">{datos.nota_churn}</p>
          {(datos.liquidaciones || []).length === 0 ? (
            <div className={`${TARJETA} text-sm text-slate-600`}>
              Todavía no tienes liquidaciones. Aparecen al cerrar cada mes.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Mes</th>
                    <th className="text-right px-3 py-2">Ganado</th>
                    <th className="text-right px-3 py-2">Recuperado</th>
                    <th className="text-right px-3 py-2">A pagarte</th>
                    <th className="text-left px-3 py-2">Desde</th>
                    <th className="text-right px-3 py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.liquidaciones.map((l, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-2">{l.periodo}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{l.bruto}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${Number(l.recuperado) < 0 ? 'text-rose-700' : ''}`}>
                        {l.recuperado}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {l.neto} {l.moneda}
                      </td>
                      <td className="px-3 py-2">{l.se_paga_desde}</td>
                      <td className="px-3 py-2 text-right">
                        {l.estado === 'paid' ? `pagada · ${l.referencia || ''}`
                          : l.estado === 'approved' ? 'autorizada' : 'en revisión'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ── Su dominio y su marca (solo marca blanca) ── */}
      {esPartner && (
        <>
          <section className="space-y-3">
            <h2 className="font-medium flex items-center gap-2">
              <Globe className="w-4 h-4 text-emerald-600" /> Tu dominio
            </h2>
            <SectionHelp
              module="dominio-socio"
              title="tu dominio propio"
              description={'Apunta un dominio tuyo a nuestra infraestructura y tus clientes '
                           + 'entran viendo tu marca desde la pantalla de acceso. Se hace en '
                           + 'dos pasos: creas los registros que te damos y le das a Comprobar.'}
              whatItDoesNot={'no toca el certificado de seguridad ni el correo — eso sale de '
                             + 'nuestra infraestructura y no se delega.'} />

            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-56">
                <label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  Dominio
                  <HelpTooltip text="Escríbelo sin http:// ni barras. Por ejemplo: crm.tumarca.com" />
                </label>
                <Input value={dominio} onChange={(e) => setDominio(e.target.value)}
                       placeholder="crm.tumarca.com" />
              </div>
              <Button onClick={guardarDominio} disabled={ocupado}>Guardar</Button>
              {dom.valor && (
                <Button variant="outline" onClick={comprobarDominio} disabled={ocupado}>
                  Comprobar
                </Button>
              )}
            </div>

            {dom.valor && (
              <div className={`${TARJETA} border-emerald-200 bg-emerald-50/50 space-y-2`}>
                <div className="text-sm font-medium">Tu acceso (y el de tus clientes)</div>
                <a href={`https://${dom.valor}/login`} target="_blank" rel="noreferrer"
                   className="inline-flex items-center h-9 px-3 text-sm rounded-md bg-[#0F172A] text-white hover:bg-[#1E293B]">
                  Abrir https://{dom.valor}/login
                </a>
                <p className="text-xs text-slate-600">
                  En Cloudflare: CNAME <b>{dom.valor.split('.')[0]}</b> → <b>app.cavernicolapp.com</b> con nube naranja,
                  y el TXT de verificación que te damos abajo. Luego pulsa Comprobar.
                </p>
              </div>
            )}
            <div className="text-sm">
              Estado:{' '}
              {dom.estado === 'verificado' ? (
                <span className="text-emerald-700 font-medium">verificado · tu marca ya se ve</span>
              ) : dom.estado === 'pendiente' ? (
                <span className="text-amber-700 font-medium">esperando tus registros DNS</span>
              ) : dom.estado === 'fallido' ? (
                <span className="text-rose-700 font-medium">no se pudo comprobar</span>
              ) : (
                <span className="text-slate-500">sin dominio configurado</span>
              )}
            </div>
            {dom.ultimo_error && (
              <p className="text-sm text-amber-800">{dom.ultimo_error}</p>
            )}

            {registros.length > 0 && (
              <div className={`${TARJETA} space-y-3`}>
                <div className="text-sm font-medium">
                  Crea estos dos registros en tu proveedor de DNS
                </div>
                {registros.map((r) => (
                  <div key={r.paso} className="border-l-4 border-emerald-300 pl-3 space-y-1">
                    <div className="text-sm font-medium">
                      Paso {r.paso} · registro {r.tipo}
                    </div>
                    <div className="text-xs text-slate-500">Nombre</div>
                    <Copiable valor={r.nombre} />
                    <div className="text-xs text-slate-500 mt-1">Valor</div>
                    <Copiable valor={r.valor} />
                    <p className="text-xs text-slate-600">{r.para_que}</p>
                  </div>
                ))}
                <p className="text-xs text-slate-500">{dom.instrucciones?.cuanto_tarda}</p>
              </div>
            )}
            <p className="text-xs text-slate-500">{datos.nota_infraestructura}</p>
          </section>

          <section className="space-y-3">
            <h2 className="font-medium flex items-center gap-2">
              <Palette className="w-4 h-4 text-indigo-600" /> Tu marca
            </h2>
            <SectionHelp
              module="marca-socio"
              title="tu marca"
              description={'El nombre y los colores que ven tus clientes al entrar por tu '
                           + 'dominio. Se aplica a todos tus negocios a la vez.'}
              whatItDoesNot={'no se ve hasta que tu dominio esté verificado. Guardarlo antes '
                             + 'está bien: se enciende solo en cuanto verifiques.'} />
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  Nombre de tu marca
                  <HelpTooltip text="Sustituye a «CavernicolApp» en toda la pantalla de tus clientes." />
                </label>
                <Input value={marca.brand_name || ''}
                       onChange={(e) => setMarca({ ...marca, brand_name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  Nombre corto
                  <HelpTooltip text="El que cabe en espacios chicos, como la pestaña del navegador." />
                </label>
                <Input value={marca.short_name || ''}
                       onChange={(e) => setMarca({ ...marca, short_name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  Correo de soporte
                  <HelpTooltip text="A dónde escriben TUS clientes cuando necesitan ayuda. Si lo dejas vacío, no se muestra ninguno." />
                </label>
                <Input value={marca.support_email || ''}
                       onChange={(e) => setMarca({ ...marca, support_email: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  Teléfono de soporte
                  <HelpTooltip text="El teléfono que ven tus clientes. Es tuyo, no el nuestro." />
                </label>
                <Input value={marca.support_phone || ''}
                       onChange={(e) => setMarca({ ...marca, support_phone: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  Color principal
                  <HelpTooltip text="Va como #1A2B3C. Es el color de la barra lateral y los títulos." />
                </label>
                <Input value={marca.primary_color || ''} placeholder="#0F172A"
                       onChange={(e) => setMarca({ ...marca, primary_color: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  Color de los botones
                  <HelpTooltip text="Va como #1A2B3C. Es el color de los botones de acción." />
                </label>
                <Input value={marca.button_color || ''} placeholder="#0F172A"
                       onChange={(e) => setMarca({ ...marca, button_color: e.target.value })} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-3 space-y-2">
                <div className="text-sm font-medium">Logotipo</div>
                <p className="text-[11px] text-slate-500">Login, correos y panel. Mejor horizontal o con fondo transparente.</p>
                {marca.logo_url && (
                  <img src={marca.logo_url + '?v=' + (marca.updated_at || Date.now())}
                       alt="Logotipo" className="max-h-12 object-contain bg-slate-50 rounded p-1" />
                )}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="text-sm"
                       onChange={(e) => subirLogo(e, 'principal')} />
              </div>
              <div className="border rounded-lg p-3 space-y-2">
                <div className="text-sm font-medium">Isotipo (favicon)</div>
                <p className="text-[11px] text-slate-500">El icono de la pestaña. Cuadrado, sin texto chico.</p>
                {marca.favicon_url && (
                  <img src={marca.favicon_url + '?v=' + (marca.updated_at || Date.now())}
                       alt="Isotipo" className="h-10 w-10 object-contain bg-slate-50 rounded" />
                )}
                <input type="file" accept="image/png,image/jpeg,image/webp,image/x-icon" className="text-sm"
                       onChange={(e) => subirLogo(e, 'isotipo')} />
              </div>
            </div>
            <Button onClick={guardarMarca} disabled={ocupado}>Guardar mi marca</Button>
          </section>

          {/* ── El número con el que le avisas a los equipos ── */}
          <section className="space-y-3">
            <h2 className="font-medium flex items-center gap-2">
              <Globe className="w-4 h-4 text-emerald-600" /> Tu número para avisar a los equipos
            </h2>
            <SectionHelp
              module="remitente-avisos-socio"
              title="con qué número se le avisa a tus vendedores"
              description={'Cuando a un vendedor de uno de tus negocios le asignan un lead o '
                           + 'le toca una tarea, el aviso sale de este número. Ponlo tuyo y el '
                           + 'aviso llega con TU marca, no con la de CavernicolApp.'}
              whatItDoesNot={'no cambia el número con el que tus negocios le escriben a SUS '
                             + 'clientes — ese lo pone cada negocio y no se toca desde aquí.'} />

            {remitente === null ? (
              <p className="text-sm text-slate-500">Cargando…</p>
            ) : !remitente.aplica ? (
              <p className="text-sm text-slate-600">{remitente.motivo}</p>
            ) : (
              <>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-sm text-slate-700 space-y-1">
                  <p className="font-medium text-emerald-900">¿Para qué es esta línea?</p>
                  <p>Le escribe a <b>tus vendedores</b> cuando les cae un lead o una tarea. No es el WhatsApp con el que tus negocios hablan con sus clientes.</p>
                  <p className="text-xs text-slate-500">Si no tienes número propio, usa el de CavernicolApp. No hay token que pegar.</p>
                </div>

                <div className="flex flex-col gap-2">
                  <label className={`border rounded-lg p-3 cursor-pointer ${remModo === 'plataforma' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200'}`}>
                    <input type="radio" className="mr-2" checked={remModo === 'plataforma'} onChange={() => setRemModo('plataforma')} />
                    <b>Usar el número de CavernicolApp</b>
                    <span className="block text-xs text-slate-500 ml-5">Recomendado para empezar. Cero tokens. Los avisos salen con nuestra línea de infraestructura.</span>
                  </label>
                  <label className={`border rounded-lg p-3 cursor-pointer ${remModo === 'propio' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200'}`}>
                    <input type="radio" className="mr-2" checked={remModo === 'propio'} onChange={() => { setRemModo('propio'); if (!remProveedor) setRemProveedor('gupshup'); }} />
                    <b>Usar mi propio WhatsApp (Gupshup / Twilio / Meta)</b>
                    <span className="block text-xs text-slate-500 ml-5">Los avisos salen con TU marca. El token lo saca TU proveedor, no CavernicolApp.</span>
                  </label>
                </div>

                {remModo === 'plataforma' && (
                  <Button onClick={usarNumeroPlataforma} disabled={ocupado}>
                    {ocupado ? 'Guardando…' : 'Usar el de CavernicolApp y seguir'}
                  </Button>
                )}

                {remModo === 'propio' && (
                  <div className="space-y-3 border rounded-lg p-3 bg-slate-50">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Proveedor</label>
                      <select className="w-full border rounded-md h-9 px-2 text-sm bg-white"
                              value={remProveedor}
                              onChange={(e) => setRemProveedor(e.target.value)}>
                        <option value="gupshup">Gupshup (WhatsApp)</option>
                        <option value="meta_whatsapp">Meta API (Embedded Signup)</option>
                        <option value="twilio_whatsapp">Twilio WhatsApp</option>
                        <option value="meta_whatsapp">Meta Cloud API</option>
                      </select>
                    </div>

                    {remProveedor === 'meta_whatsapp' && (
                      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-2">
                        <p className="text-xs text-blue-950">
                          Somos Tech Provider: se abre la ventana de Meta, eliges el número y listo.
                          <b> Ese WABA debe tener método de pago en Meta</b> — el consumo lo cobra Meta a ESA cuenta, no a CavernicolApp.
                        </p>
                        <MetaWhatsappEmbeddedSignup
                          configUrl="/partner/infraestructura/meta/embedded-signup/config"
                          completeUrl="/partner/infraestructura/meta/embedded-signup/complete"
                          avisoPago="Obligatorio: forma de pago directa en el WABA (Business Manager → Métodos de pago). Sin eso Meta no deja enviar."
                          onConnected={() => cargar()}
                        />
                      </div>
                    )}
                    {remProveedor === 'gupshup' && (
                      <div className="text-xs text-slate-600 space-y-2">
                        <p>Entra a <a className="underline" href="https://www.gupshup.io/developer/login" target="_blank" rel="noreferrer">gupshup.io → Dashboard</a> con la cuenta de <b>tu empresa</b> (no la de CavernicolApp) y copia esto:</p>
                        <ol className="list-decimal ml-4 space-y-1">
                          <li><b>Nombre de la app</b> — el nombre que le pusiste a la app de WhatsApp (ej. XenIA-Avisos).</li>
                          <li><b>API Key</b> — Dashboard → Settings / API Key. Es una cadena larga. <i>Ese es el “token”. No te lo damos nosotros.</i></li>
                          <li><b>Número</b> — el WhatsApp ya aprobado por Meta en esa app, con lada (521…).</li>
                        </ol>
                        <p>Usa un número <b>distinto</b> al de tus clientes. Si es el mismo, se mezclan los chats del vendedor y del lead.</p>
                      </div>
                    )}

                    {remProveedor === 'gupshup' && (
                      <div className="grid md:grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Nombre de la app</label>
                          <Input value={remAppName} placeholder="XenIA-Avisos"
                                 onChange={(e) => setRemAppName(e.target.value)} />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">API Key (el token de Gupshup)</label>
                          <Input type="password" autoComplete="new-password"
                                 value={remClave} placeholder="pega aquí la API Key"
                                 onChange={(e) => setRemClave(e.target.value)} />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Número de WhatsApp</label>
                          <Input value={remIdentidad} placeholder="5215512345678"
                                 onChange={(e) => setRemIdentidad(e.target.value)} />
                        </div>
                      </div>
                    )}

                    {remProveedor !== 'gupshup' && (
                      <div className="grid md:grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Credencial</label>
                          <Input type="password" value={remClave} placeholder="SID + token o token de Meta"
                                 onChange={(e) => setRemClave(e.target.value)} />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Número</label>
                          <Input value={remIdentidad} placeholder="+52 55 1234 5678"
                                 onChange={(e) => setRemIdentidad(e.target.value)} />
                        </div>
                      </div>
                    )}

                    <Button onClick={guardarRemitente} disabled={ocupado}>
                      {ocupado ? 'Guardando…' : 'Conectar mi línea'}
                    </Button>
                  </div>
                )}

                {/* ── Su logo en el encabezado del aviso ──
                    Va aparte del número a propósito: no depende de tener
                    credenciales, y es lo que el socio puede resolver el mismo
                    día que firma. */}
                <div className="mt-4 border-t pt-4 flex items-start gap-4 flex-wrap">
                  {remitente.remitente?.logo_url ? (
                    <img src={remitente.remitente.logo_url} alt="Tu logo"
                         className="h-20 w-20 rounded-md border object-contain shrink-0" />
                  ) : (
                    <div className="h-20 w-20 rounded-md border border-dashed shrink-0
                                    flex items-center justify-center text-slate-400 text-xs text-center px-1">
                      Sin logo
                    </div>
                  )}
                  <div className="flex-1 min-w-56 space-y-2">
                    <label className="text-xs text-slate-500 flex items-center gap-1">
                      Tu logo en el aviso
                      <HelpTooltip text="Es la imagen que verán tus vendedores arriba del mensaje. Sube el logo que ya tienes: si es horizontal o con fondo transparente lo acomodamos en un cuadrado con fondo blanco, que es lo que WhatsApp necesita." />
                    </label>
                    <input type="file" accept=".png,.jpg,.jpeg,.webp"
                           disabled={ocupado}
                           className="text-sm"
                           onChange={(e) => { subirMiLogo(e.target.files?.[0]); e.target.value = ''; }} />
                    <p className="text-xs text-slate-500">
                      PNG, JPG o WEBP, hasta 3 MB. Sin logo, el aviso sale solo con texto.
                    </p>
                    {conversionLogo && (
                      <p className="text-xs text-slate-600 bg-slate-50 rounded p-2">
                        Tu archivo llegó de {conversionLogo.medidas_originales} píxeles
                        {conversionLogo.era_cuadrada ? ' y ya era cuadrado.' :
                          ', no era cuadrado, así que lo centramos sin recortarlo.'}
                        {conversionLogo.tenia_transparencia &&
                          ` Tenía fondo transparente y le pusimos ${conversionLogo.fondo_aplicado}.`}
                        {' '}Así lo verán tus vendedores.
                      </p>
                    )}
                    {remitente.remitente?.tiene_logo && (
                      <Button variant="ghost" size="sm" onClick={quitarMiLogo} disabled={ocupado}>
                        Quitar logo
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>
        </>
      )}

      {/* ── Su forma de pago ── */}
      {esPartner && (
        <section className={`${TARJETA} ${pago && pago.al_corriente === false ? 'border-rose-300 bg-rose-50/60' : ''}`}
                 data-testid="forma-de-pago">
          <h2 className="font-medium flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Tu forma de pago
            <HelpTooltip text={'Tu tarjeta la guarda Stripe, no nosotros: nunca vemos el número. '
              + 'Por eso el cobro sigue funcionando si te reponen la tarjeta, y por eso aquí '
              + 'solo ves la marca y los últimos cuatro dígitos.'} />
          </h2>

          {/* Este límite se dice a la vista: es lo que el socio necesita saber
              para no llevarse un susto cuando dé de alta un cliente nuevo. */}
          <p className="text-xs text-slate-500 mt-1">
            Se te cobra <b>una vez al mes</b>: tus clientes activos o tu mínimo, lo que
            sea mayor. Si das de alta un cliente a media quincena <b>no se te cobra ese
            día</b> — entra en tu recibo del mes siguiente.
          </p>

          {pago === null && <p className="text-sm text-slate-500 mt-2">Consultando…</p>}
          {pago?.error && (
            <p className="text-sm text-rose-700 mt-2">No se pudo consultar: {pago.error}</p>
          )}
          {pago && !pago.error && (
            <div className="mt-3 space-y-2 text-sm">
              {pago.tarjeta ? (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span>
                      Tienes puesta una tarjeta {pago.tarjeta.marca} terminada en
                      {' '}<b>{pago.tarjeta.ultimos4}</b>
                    </span>
                  </div>
                  <Button variant="outline" size="sm" onClick={ponerMiTarjeta} disabled={poniendoTarjeta}>
                    {poniendoTarjeta ? 'Abriendo Stripe…' : 'Cambiar tarjeta'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={quitarMiTarjeta} disabled={poniendoTarjeta}>
                    Quitar
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 text-amber-900">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                      <b>Todavía no tienes tarjeta.</b> Sin ella no podemos hacerte
                      el cargo mensual de tus licencias. Se guarda en Stripe: nosotros
                      nunca vemos el número. <b>Hoy no se cobra nada.</b>
                    </span>
                  </div>
                  <Button
                    onClick={ponerMiTarjeta}
                    disabled={poniendoTarjeta}
                    className="bg-[#0F172A] text-white hover:bg-[#1E293B]"
                    data-testid="partner-add-card"
                  >
                    {poniendoTarjeta ? 'Abriendo Stripe…' : 'Agregar mi tarjeta'}
                  </Button>
                </div>
              )}
              <div className="text-slate-600">
                Se te cobran <b>{pago.licencias_que_se_cobran}</b> licencia(s) ×
                {' '}{pago.precio} {pago.moneda} al mes.
              </div>
              {pago.al_corriente === false && (
                <div className="text-rose-800">
                  {pago.ultimo_error || 'Tu último cobro no pasó. Revisa tu tarjeta.'}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {esPartner && (
        <section className="space-y-2">
          <section className="space-y-3">
            <h2 className="font-medium">Tu infraestructura</h2>
            <p className="text-sm text-slate-600">
              Twilio: el número que <b>enlaza la llamada</b> lead ↔ vendedor.
              OpenAI/Grok: tu llave y el modelo de tus paquetes.
              El WhatsApp que avisa a vendedores está arriba, en «Tu número para avisar a los equipos».
              La IA de voz se configura en cada tenant.
            </p>
            <Link to="/socio/planes" className="inline-flex text-sm font-medium text-indigo-700 underline">
              Abrir el constructor de paquetes →
            </Link>
            <div className="grid md:grid-cols-2 gap-3">
              {infra.map((p) => (
                <form key={p.key} className={`${TARJETA} space-y-2`} onSubmit={async (e) => {
                  e.preventDefault();
                  const fd = new FormData(e.target);
                  const cuerpo = {};
                  p.campos.forEach((c) => { cuerpo[c.key] = fd.get(c.key); });
                  setOcupado(true);
                  try {
                    const { data } = await api.put(`/partner/infraestructura/${p.key}`, cuerpo);
                    toast.success(data.aviso);
                    const r = await api.get('/partner/infraestructura');
                    setInfra(r.data?.proveedores || []);
                  } catch (err) { toast.error(formatApiError(err)); }
                  finally { setOcupado(false); }
                }}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm">{p.nombre}</div>
                    <span className={`text-[11px] ${p.conectado ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {p.conectado ? 'Conectado' : 'Sin conectar'}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500">{p.para_que}</p>
                  {p.campos.map((c) => (
                    <div key={c.key}>
                      <label className="text-[11px] text-slate-500">{c.label}</label>
                      {c.opciones ? (
                        <select name={c.key} defaultValue={p.valores?.[c.key] || c.opciones[0]}
                                className="w-full border rounded-md h-10 px-2 text-sm">
                          {c.opciones.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <Input name={c.key} type={c.secreto ? 'password' : 'text'}
                               defaultValue={p.valores?.[c.key] || ''}
                               placeholder={c.secreto && p.conectado ? '•••• (sin cambiar)' : ''} />
                      )}
                    </div>
                  ))}
                  <Button type="submit" disabled={ocupado} className="w-full">Guardar</Button>
                </form>
              ))}
            </div>
          </section>

          <h2 className="font-medium flex items-center justify-between">Tus paquetes
              <Link to="/socio/planes" className="text-sm font-medium text-indigo-700">Constructor de paquetes →</Link>
            </h2>

          <p className="text-xs text-slate-500">
            Eliges un molde de CavernicolApp (las funciones) y le pones TU nombre y TU precio. Si lo personalizas se crea una copia tuya. Tus clientes gastan de TU WhatsApp, voz e IA — no de la nuestra.
            Nosotros te seguimos cobrando la licencia del trato; el margen es tuyo.
          </p>
          <div className={`${TARJETA} grid md:grid-cols-4 gap-2 items-end`}>
            <div>
              <label className="text-[11px] text-slate-500">Plan de CavernicolApp</label>
              <select className="w-full h-9 border rounded-md px-2 text-sm bg-white"
                      value={nuevoPaq.plan_id}
                      onChange={(e) => setNuevoPaq({ ...nuevoPaq, plan_id: e.target.value })}>
                <option value="">Elegir…</option>
                {(catalogo.planes || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Nombre que verá tu cliente</label>
              <Input value={nuevoPaq.name} onChange={(e) => setNuevoPaq({ ...nuevoPaq, name: e.target.value })}
                     placeholder="Xen-IA Pro" />
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Precio mensual (tú se lo cobras)</label>
              <Input value={nuevoPaq.price} onChange={(e) => setNuevoPaq({ ...nuevoPaq, price: e.target.value })}
                     placeholder="1490" />
            </div>
            <Button disabled={ocupado || !nuevoPaq.plan_id || !nuevoPaq.price}
                    onClick={async () => {
                      setOcupado(true);
                      try {
                        const { data } = await api.post('/partner/paquetes/personalizar', nuevoPaq);
                        toast.success(data.aviso);
                        const r = await api.get('/partner/paquetes');
                        setPaquetes(r.data?.paquetes || []);
                        setNuevoPaq({ plan_id: '', name: '', price: '' });
                      } catch (e) { toast.error(formatApiError(e)); }
                      finally { setOcupado(false); }
                    }}>
              Guardar paquete
            </Button>
          </div>
          {paquetes.length > 0 && (
            <div className="space-y-1">
              {paquetes.map((p) => (
                <div key={p.id} className="flex flex-wrap justify-between items-center gap-2 border rounded p-2 text-sm bg-white">
                  <div className="min-w-0">
                    <b>{p.nombre}</b>
                    <span className="text-slate-500"> · {p.es_propio ? 'paquete propio' : ('molde ' + p.plan_de_casa)}</span>
                    <span className="tabular-nums text-slate-700"> · {p.precio} {p.moneda}/mes</span>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {p.es_propio ? (
                      <Link to={`/socio/planes?editar=${p.plan_id}`} className="text-xs px-2 py-1 border rounded hover:bg-slate-50">Editar</Link>
                    ) : (
                      <button type="button" className="text-xs px-2 py-1 border rounded hover:bg-slate-50" onClick={() => setEditPaq(p)}>Precio</button>
                    )}
                    <button type="button" className="text-xs px-2 py-1 border rounded text-red-700 hover:bg-red-50"
                      onClick={async () => {
                        if (!window.confirm('¿Quitar el paquete «' + p.nombre + '»? Si hay negocios usándolo te lo vamos a pedir confirmar.')) return;
                        setOcupado(true);
                        try {
                          await api.delete('/partner/paquetes/' + p.id);
                          toast.success('Paquete eliminado');
                          const r = await api.get('/partner/paquetes');
                          setPaquetes(r.data?.paquetes || []);
                        } catch (e) {
                          const d = e.response?.data?.detail || '';
                          if (e.response?.status === 409) {
                            if (window.confirm(String(d) + ' Forzar y quitarles el paquete?')) {
                              try {
                                await api.delete('/partner/paquetes/' + p.id, { params: { force: true } });
                                toast.success('Paquete eliminado');
                                const r = await api.get('/partner/paquetes');
                                setPaquetes(r.data?.paquetes || []);
                              } catch (e2) { toast.error(formatApiError(e2)); }
                            }
                          } else toast.error(formatApiError(e));
                        } finally { setOcupado(false); }
                      }}>Eliminar</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Su cartera ── */}
      <section className="space-y-2">
        <h2 className="font-medium flex items-center gap-2">
          <Building2 className="w-4 h-4" /> Tus negocios
          <HelpTooltip text="Los das de alta tú. Cada negocio vivo cuenta como una licencia de tu trato." />
        </h2>
        <p className="text-xs text-slate-500">
          Das de alta al cliente, le asignas un paquete tuyo y puedes entrar a configurarlo.
          Cada negocio activo suma una licencia (o ya está cubierta por tu mínimo).
        </p>
        {esPartner && (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setMostrandoAlta((v) => !v)}>
              {mostrandoAlta ? 'Cerrar' : 'Dar de alta un negocio'}
            </Button>
          </div>
        )}
        {mostrandoAlta && (
          <div className={`${TARJETA} grid md:grid-cols-2 gap-2`}>
            <div>
              <label className="text-[11px] text-slate-500">Nombre del negocio</label>
              <Input value={nuevoNeg.name} onChange={(e) => setNuevoNeg({ ...nuevoNeg, name: e.target.value })} />
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Vertical</label>
              <select className="w-full h-9 border rounded-md px-2 text-sm bg-white"
                      value={nuevoNeg.vertical}
                      onChange={(e) => setNuevoNeg({ ...nuevoNeg, vertical: e.target.value })}>
                <option value="">Elegir…</option>
                {(catalogo.verticales || []).map((v) => (
                  <option key={v.key} value={v.key}>{v.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Administrador (nombre)</label>
              <Input value={nuevoNeg.owner_name} onChange={(e) => setNuevoNeg({ ...nuevoNeg, owner_name: e.target.value })} />
            </div>
            <div>
              <label className="text-[11px] text-slate-500">Administrador (correo)</label>
              <Input value={nuevoNeg.owner_email} onChange={(e) => setNuevoNeg({ ...nuevoNeg, owner_email: e.target.value })}
                     placeholder="admin@suempresa.com" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] text-slate-500">Paquete que le vendes</label>
              <select className="w-full h-9 border rounded-md px-2 text-sm bg-white"
                      value={nuevoNeg.plan_id}
                      onChange={(e) => setNuevoNeg({ ...nuevoNeg, plan_id: e.target.value })}>
                <option value="">El de casa (por defecto)</option>
                {paquetes.filter((p) => p.activo).map((p) => (
                  <option key={p.plan_id} value={p.plan_id}>{p.nombre} · {p.precio} {p.moneda}</option>
                ))}
                {editPaq && (
            <div className={`${TARJETA} space-y-3`}>
              <div className="font-medium">Constructor: {editPaq.nombre}</div>
              <p className="text-xs text-slate-500">Topes de TU paquete. -1 = ilimitado. Esto no gasta de CavernicolApp.</p>
              {['max_users','max_branches','max_leads_monthly','max_ai_tokens_monthly','max_voice_minutes_monthly','max_whatsapp_conversations_monthly'].map((k) => {
                const v = (editPaq.limits || {})[k];
                const inf = v === -1 || v === '-1';
                return (
                  <div key={k} className="flex items-center gap-2">
                    <label className="text-xs w-56">{k.replace(/max_|_/g,' ')}</label>
                    <Input className="w-28" disabled={inf}
                           value={inf ? '' : (v ?? '')}
                           onChange={(e) => setEditPaq({ ...editPaq, limits: { ...editPaq.limits, [k]: e.target.value } })} />
                    <label className="text-xs flex items-center gap-1">
                      <input type="checkbox" checked={inf}
                             onChange={(e) => setEditPaq({ ...editPaq, limits: { ...editPaq.limits, [k]: e.target.checked ? -1 : 0 } })} />
                      Ilimitado
                    </label>
                  </div>
                );
              })}
              <div className="flex gap-2">
                <Button onClick={async () => {
                  setOcupado(true);
                  try {
                    const { data } = await api.put(`/partner/paquetes/${editPaq.id}`, {
                      name: editPaq.nombre, price: editPaq.precio, limits: editPaq.limits,
                      entitlements: { ...(editPaq.entitlements || {}), platform_infrastructure_enabled: false },
                    });
                    toast.success(data.aviso);
                    const r = await api.get('/partner/paquetes');
                    setPaquetes(r.data?.paquetes || []);
                    setEditPaq(null);
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setOcupado(false); }
                }}>Guardar paquete</Button>
                <Button variant="outline" onClick={() => setEditPaq(null)}>Cerrar</Button>
              </div>
            </div>
          )}
          {paquetes.length === 0 && (catalogo.planes || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <Button disabled={ocupado || !nuevoNeg.name || !nuevoNeg.vertical || !nuevoNeg.owner_email}
                      onClick={async () => {
                        setOcupado(true);
                        try {
                          const { data } = await api.post('/partner/negocios', nuevoNeg);
                          toast.success(data.aviso);
                          setMostrandoAlta(false);
                          setNuevoNeg({ name: '', vertical: '', owner_name: '', owner_email: '', plan_id: '' });
                          const r = await api.get('/partner/mi-cuenta');
                          setDatos(r.data);
                        } catch (e) { toast.error(formatApiError(e)); }
                        finally { setOcupado(false); }
                      }}>
                Crear negocio
              </Button>
            </div>
          </div>
        )}
        {(s.negocios || []).length === 0 ? (
          <div className={`${TARJETA} text-sm text-slate-600`}>
            Todavía no tienes negocios. Da de alta el primero: cuenta como una licencia de tu trato.
          </div>
        ) : (
          <div className="space-y-1">
            {s.negocios.map((n) => (
              <div key={n.id} className="border rounded p-2 text-sm bg-white space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span>{n.nombre} <span className="text-slate-500">· {n.vertical}</span></span>
                  <div className="flex items-center gap-2">
                    <span className={n.cuenta_como_licencia ? 'text-emerald-700' : 'text-slate-500'}>
                      {n.cuenta_como_licencia ? 'cuenta para tu cuenta' : `no cuenta (${n.estado})`}
                    </span>
                    <Button size="sm" variant="outline"
                            onClick={async () => {
                              try {
                                const { data } = await api.post(`/partner/negocios/${n.id}/entrar`);
                                toast.success(data.aviso);
                                window.location.href = data.destino || '/tenant';
                              } catch (e) { toast.error(formatApiError(e)); }
                            }}>
                      Entrar
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="flex-1 min-w-[160px]">
                    <label className="text-[11px] text-slate-500">Número / chip de ese negocio</label>
                    <Input className="h-8 text-sm" placeholder="52155..."
                           value={telNeg[n.id] || ''}
                           onChange={(e) => setTelNeg({ ...telNeg, [n.id]: e.target.value })} />
                  </div>
                  <Button size="sm" disabled={ocupado || !(telNeg[n.id] || '').trim()}
                    onClick={async () => {
                      setOcupado(true);
                      try {
                        const { data } = await api.post(`/partner/negocios/${n.id}/conectar-whatsapp`, { telefono: telNeg[n.id] });
                        toast.success(data.aviso);
                        if (data.liga_meta) window.open(data.liga_meta, '_blank');
                      } catch (e) { toast.error(formatApiError(e)); }
                      finally { setOcupado(false); }
                    }}>
                    Convertir a WhatsApp Business
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500">
                  Solo pones el número. CavernicolApp lo convierte a WABA en nuestra cuenta Gupshup.
                  Los mensajes se descuentan de la bolsa que nos compraste — no es gratis.
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
